import React, { Component } from 'react';
import Typography from '@material-ui/core/Typography/Typography';
import AppBar from '@material-ui/core/AppBar/AppBar';
import Toolbar from '@material-ui/core/Toolbar/Toolbar';
import IconButton from '@material-ui/core/IconButton';
import BackArrow from '@material-ui/icons/ArrowBackIos';
import { withStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField/TextField';
import Button from '@material-ui/core/Button/Button';
import InputMask from 'react-input-mask';
import firebase from 'firebase/app';
import Snackbar from '@material-ui/core/Snackbar';
import DeleteIcon from '@material-ui/icons/DeleteForever';
import PropTypes from 'prop-types';

import SnackbarContentWrapper from '../../Components/SnackbarContentWrapper/SnackbarContentWrapper';
import './Recipient.css';
import { db, getUser, redirectToRoot } from '../../utilities/firebase-config';
import styles from './Recipient.style';

const defaultState = {
  nameError: false,
  phoneError: false,
  recipient: {
    added: 0,
    name: '',
    phone: '',
    updated: false,
  },
  recipientId: null,
  snackbar: {
    message: '',
    open: false,
    variant: 'success',
  },
  submitting: false,
  title: '',
};

/**
 * Save a recipient to the notifyList path
 * @param recipientId
 * @param recipient
 * @returns {Promise<string | string | *>}
 */
const saveChanges = async ({ recipientId, recipient }) => {
  // Prevent saving the phone number inside the recipient body
  const newRecipient = {
    ...recipient,
  };

  // If creating a new recipient, make sure the phone number is not already in user
  if (recipientId !== newRecipient.phone) {
    const snapshot = await db.ref(`notifyList/${newRecipient.phone}`).once('value');
    if (snapshot.val()) {
      throw new Error('Phone Number Is Already In Use');
    }
  }

  if (!recipientId) {
    newRecipient.added = firebase.database.ServerValue.TIMESTAMP;
  } else {
    newRecipient.updated = firebase.database.ServerValue.TIMESTAMP;
  }

  delete newRecipient.phone;

  // Save changes
  await db.ref(`notifyList/${recipient.phone}`).set(newRecipient)
    .catch(() => {
      throw new Error('Failed to Save Changes');
    });

  return recipient.phone;
};

const deleteOldRecipient = async ({ recipientId }) => {
  if (recipientId) {
    await db.ref(`notifyList/${recipientId}`).set(null)
      .catch(() => {
        throw new Error('Failed to Delete Recipient');
      });
  }
};

class Recipient extends Component {
  state = defaultState;

  async componentDidMount() {
    const user = await getUser();
    const { history } = this.props;

    if (!user || !user.profile || user.profile.permissions !== 'Administrator') {
      redirectToRoot(history);
    }

    let recipientId = window.location.pathname.split('/')[2];
    if (recipientId === 'new') {
      recipientId = null;
    }

    if (recipientId) {
      this.setState({
        recipientId,
      });
    } else {
      this.setState({
        title: 'Add Recipient',
      });
    }

    this.watchRecipient(recipientId);
  }

  componentWillUnmount() {
    const { recipientId } = this.state;
    this.unWatchRecipient(recipientId);
  }

  deleteRecipient = () => {
    const { recipientId } = this.state;
    try {
      deleteOldRecipient({ recipientId });

      this.setState({
        ...defaultState,
        snackbar: {
          message: 'Recipient Deleted',
          open: true,
          variant: 'success',
        },
        submitting: true,
      });

      setTimeout(() => {
        this.goBack();
      }, 3000);
    } catch (e) {
      this.setState({
        snackbar: {
          message: e.toString().replace('Error: ', ''),
          open: true,
          variant: 'error',
        },
        submitting: false,
      });

      return false;
    }

    return null;
  };

  submit = async (event) => {
    event.preventDefault();
    const { recipientId: originalRecipientId } = this.state;
    const { recipient } = this.state;
    const { history } = this.props;

    let nameError = null;
    let phoneError = null;
    let phone = recipient.phone.replace(/[^0-9]+/g, '');

    if (phone.length !== 10) {
      phoneError = 'Please enter a valid, 10 digit phone number';
    }

    if (recipient.name.length < 2) {
      nameError = 'Please enter the recipient\'s full name';
    }

    if (recipient.name.length > 75) {
      nameError = 'Please enter a name less than 75 characters';
    }

    // Stop if there are any form errors
    if (nameError || phoneError) {
      this.setState({
        nameError,
        phoneError,
      });

      return false;
    }

    this.setState({
      submitting: true,
    });

    // Make the phone number international again
    phone = `+1${phone}`;

    let newRecipientId;
    try {
      // When the changes are made, the newRecipientId will be the new(or existing) phone number
      newRecipientId = await saveChanges({
        recipient: {
          ...recipient,
          phone,
        },
        recipientId: originalRecipientId,
      });
    } catch (e) {
      this.setState({
        snackbar: {
          message: e.toString().replace('Error: ', ''),
          open: true,
          variant: 'error',
        },
        submitting: false,
      });

      return false;
    }

    // Show snackbar
    this.setState({
      recipientId: phone,
      snackbar: {
        message: 'Changes Saved!',
        open: true,
        variant: 'success',
      },
      submitting: false,
    });

    // Delete the original recipient if they have a new phone number
    if (newRecipientId !== originalRecipientId) {
      await deleteOldRecipient({
        recipientId: originalRecipientId,
      });

      const location = {
        pathname: `${newRecipientId}`,
      };

      this.unWatchRecipient(originalRecipientId);
      this.watchRecipient(newRecipientId);

      // Redirect to the new location
      history.replace(location);
    }

    return false;
  };

  changeField = (event) => {
    const { recipient } = this.state;

    this.setState({
      nameError: false,
      phoneError: false,
      recipient: {
        ...recipient,
        [event.target.name]: event.target.value,
      },
    });
  };

  closeSnackbar = () => {
    const { snackbar } = this.state;

    this.setState({
      snackbar: {
        ...snackbar,
        open: false,
      },
    });
  };

  watchRecipient = (recipientId) => {
    db.ref(`notifyList/${recipientId}`).on('value', (snapshot) => {
      const data = snapshot.val();

      if (data) {
        data.phone = recipientId.replace('+1', '');
        this.setState({
          recipient: data,
          title: data.name,
        });
      }
    });
  };

  unWatchRecipient = (recipientId) => {
    db.ref(`notifyList/${recipientId}`).off();
  };

  goBack = () => {
    const { history } = this.props;
    history.goBack();
  };

  render() {
    const { classes } = this.props;
    const {
      nameError, phoneError, submitting, recipient, snackbar, title,
    } = this.state;

    return (
      <div className="Recipient container">

        <AppBar position="fixed" color="primary">
          <Toolbar>

            <IconButton className={ classes.menuButton } color="inherit" aria-label="Go Back" onClick={ this.goBack }>
              <BackArrow />
            </IconButton>

            <Typography variant="h6" color="inherit" className={ classes.grow }>
              { title }
            </Typography>

            <IconButton aria-label="Add Recipient" className={ classes.deleteButton } onClick={ this.deleteRecipient }>
              <DeleteIcon />
            </IconButton>

          </Toolbar>
        </AppBar>

        <Snackbar
          anchorOrigin={ {
            horizontal: 'left',
            vertical: 'bottom',
          } }
          open={ snackbar.open }
          autoHideDuration={ 3000 }
          onClose={ this.closeSnackbar }
        >
          <SnackbarContentWrapper
            onClose={ this.closeSnackbar }
            variant={ snackbar.variant }
            message={ snackbar.message }
          />
        </Snackbar>

        <Typography gutterBottom variant="h5" component="h2">
          Edit Recipient
        </Typography>

        <form onSubmit={ this.submit } name="edit-recipient">

          <TextField
            className={ classes.textField }
            id="name"
            label="Full Name"
            margin="normal"
            name="name"
            onChange={ this.changeField }
            value={ recipient.name }
            type="text"
          />

          { nameError
          && <div className={ classes.error }>{ nameError }</div>
          }

          <InputMask
            mask="(999) 999 9999"
            value={ recipient.phone }
            onChange={ this.changeField }
          >
            {() => (
              <TextField
                id="phone"
                name="phone"
                label="Phone Number"
                className={ classes.textField }
                margin="normal"
                type="tel"
              />
            )}
          </InputMask>

          { phoneError
          && <div className={ classes.error }>{ phoneError }</div>
          }

          <Button
            variant="outlined"
            color="primary"
            className={ classes.button }
            onClick={ this.submit }
            type="submit"
            disabled={ submitting }
          >
            Save
          </Button>

        </form>

      </div>
    );
  }
}

Recipient.propTypes = {
  classes: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
};

export default withStyles(styles)(Recipient);
